import type {
  BlobLayoutStyledBlobPayload,
  BlobLayoutStyledBlobResponse,
  DataRouterBlobLayoutPayload,
  DeferredMetadata,
} from '@github-ui/code-view-types'
import {getAppTypeHeader} from '@github-ui/fetch-headers'
import {blobLayoutPath, blobLayoutStyledPath, deferredMetadataPath} from '@github-ui/paths'
import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {QueryRouteQueryType} from '@github-ui/react-core/future/data-router-types'
import {type JsonResponse, mainQuery} from '@github-ui/react-core/future/main-query'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'

import {codeViewAppBuilder} from '../config/app-builder'
import {CodeViewPathIsDirectoryError} from '../pages/CodeViewBlobLayoutErrorBoundary'
import {latestCommitQuery} from '../queries/latest-commit-query'
import {codeViewTreeRoute} from './code-view-tree-route'

type BlobRouteParams = {
  owner: string
  repo: string
  branch: string
  '*': string
}

function throwError(json: Pick<JsonResponse, 'payload'>, routeId: string): never {
  if (json.payload?.[codeViewTreeRoute.id]) {
    throw new CodeViewPathIsDirectoryError()
  }

  throw new Error(`Unable to find payload for id: ${routeId}`)
}

export function buildCodeViewBlobLayoutRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('codeViewBlobLayoutRoute', {
    path: '',
    queries: [
      mainQuery<DataRouterBlobLayoutPayload>({
        queryDeps: deps => {
          const params = deps.params as BlobRouteParams
          const shortPath = deps.searchParams.get('short_path')
          return {
            pathname: blobLayoutPath({
              repo: {
                ownerLogin: params.owner,
                name: params.repo,
              },
              commitish: params.branch,
              path: params['*'] || '',
            }),
            // eslint-disable-next-line camelcase
            ...(shortPath ? {searchParams: {short_path: shortPath}} : {}),
          }
        },
        queryFn: async ({routeId, queryDeps}) => {
          // Merge the appTypeHeader into any existing headers in queryDeps.init, if present
          const appTypeHeader = getAppTypeHeader('dataRouter')
          const mergedQueryDeps = {
            ...queryDeps,
            init: {
              ...queryDeps?.init,
              headers: {...appTypeHeader, ...queryDeps?.init?.headers},
            },
          }

          const json = await queryFnFetch<JsonResponse>({
            queryDeps: mergedQueryDeps,
          })

          const routePayload = json.payload?.[routeId]
          if (!routePayload) {
            throwError(json, routeId)
          }

          return {
            meta: json.meta,
            payload: routePayload,
          } as unknown as DataRouterBlobLayoutPayload // A select will run internally to extract the payload correctly.
        },
      }),
      {
        queryName: 'codeViewBlobLayoutRoute.StyledBlob',
        queryDeps: deps => {
          const params = deps.params as BlobRouteParams
          return {
            pathname: blobLayoutStyledPath({
              repo: {
                ownerLogin: params.owner,
                name: params.repo,
              },
              commitish: params.branch,
              path: params['*'] || '',
            }),
          }
        },
        queryFn: async queryKey => queryFnFetch<BlobLayoutStyledBlobResponse>(queryKey),
        // Normalize mismatched queryFn response (with envelope) vs embeddedData payload (already unwrapped)
        select: (json: BlobLayoutStyledBlobResponse | BlobLayoutStyledBlobPayload) => {
          // Embedded data is already unwrapped, return as is
          if ('colorizedLines' in json && 'rawLines' in json) return json
          // Fetch data is wrapped in a JsonResponse, unwrap it
          const payload = json.payload?.['codeViewBlobLayoutRoute.StyledBlob']
          if (payload === undefined || payload === null) {
            throwError(json, 'codeViewBlobLayoutRoute.StyledBlob')
          }
          return payload
        },
        type: QueryRouteQueryType.Deferred,
      },
      {
        queryName: 'deferredMetadata',
        queryDeps: ({params: routeParams}) => {
          const params = routeParams as BlobRouteParams

          return {
            pathname: deferredMetadataPath({
              repo: {
                ownerLogin: params.owner,
                name: params.repo,
              },
              commitish: params.branch,
              path: params['*'] || '',
            }),
          }
        },
        queryFn: async queryKey => queryFnFetch<DeferredMetadata>(queryKey),
        type: QueryRouteQueryType.Deferred,
      },
      latestCommitQuery,
    ],
    shouldRevalidate: ({currentParams, nextParams, currentUrl, nextUrl}) => {
      return (
        currentParams.owner !== nextParams.owner ||
        currentParams.repo !== nextParams.repo ||
        currentParams.branch !== nextParams.branch ||
        currentParams['*'] !== nextParams['*'] ||
        currentUrl.searchParams.get('short_path') !== nextUrl.searchParams.get('short_path')
      )
    },
    uiServiceOptions: {
      tsrConfig: {
        // This pathless route depends on child path
        // params (branch, *) that aren't part of its own match identity, so
        // the loader must reload on every navigation.
        shouldReload: true,
        validateSearch: (search: Record<string, unknown>): {short_path?: string} => search as {short_path?: string},
      },
    },
  })
}

export const codeViewBlobLayoutRoute = buildCodeViewBlobLayoutRoute(codeViewAppBuilder)
